import { z } from "zod";
import { isValidPythonRegex } from "./is-valid-python-regex";
import {
  partnerEventSchema,
  validControlCategorySchema,
  validControlSchema,
} from "./validators";

export const odataPropertyFns = ["length"] as const;

export const odataScopes = [
  "instances",
  "failures",
  "excepted",
  "groups",
] as const;
export const odataQuantifierSchema = z
  .enum(["any", "all"])
  .optional()
  .catch("any");
export const odataWithSchema = z.object({
  instance_id: z.string().optional(),
  category: validControlCategorySchema.or(z.null()).optional(),
  control: validControlSchema.optional(),
  event: partnerEventSchema.optional(),
});
export const odataOperatorSchema = z.enum([
  "contains",
  "does not contain",
  "matches",
  "does not match",
  "greater than",
  "less than",
  "is",
  "is not",
]);
export const odataObjectValueSchema = z.object({
  type: z.enum(["guid", "duration", "binary", "decimal", "raw"]),
  value: z.string().nonempty(),
});
export type OdataObjectValue = z.infer<typeof odataObjectValueSchema>;
export const odataValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  odataObjectValueSchema,
]);
export const odataDefinitionSchema = z.object({
  id: z.string(),
  scope: z.enum(odataScopes).optional(),
  quantifier: odataQuantifierSchema,
  with: odataWithSchema.optional(),
  sort: z.string().or(z.array(z.string())).optional(),
});

export type OdataValue = z.infer<typeof odataValueSchema>;
export type OdataOperator = z.infer<typeof odataOperatorSchema>;
export type OdataDefinition = z.infer<typeof odataDefinitionSchema>;
export type OdataPropertyFn = (typeof odataPropertyFns)[number];
export type OdataQuantifier = z.infer<typeof odataQuantifierSchema>;
export type OdataScope = (typeof odataScopes)[number];
export type OdataWith = z.infer<typeof odataWithSchema>;

export const filterPropertyFunctionSchema = z.enum(odataPropertyFns);

const baseFilterSchema = z.object({
  operator: odataOperatorSchema,
  value: odataValueSchema,
});

export type BaseFilter = z.infer<typeof baseFilterSchema>;

export const filterSchema = odataDefinitionSchema
  .extend(baseFilterSchema.shape)
  .extend({
    fn: filterPropertyFunctionSchema.optional(),
    isArray: z.boolean().optional(),
    ors: z.array(baseFilterSchema).optional(),
  });

export type Filter = z.infer<typeof filterSchema>;
export type ObjectFilter = Omit<Filter, "value"> & { value: OdataObjectValue };

const qsFilterSchema = filterSchema.refine(
  (input: Filter) =>
    (input.operator !== "matches" && input.operator !== "does not match") ||
    typeof input.value !== "string" ||
    isValidPythonRegex(input.value),
  { message: "Invalid regular expression", path: ["value"] },
);

export const filtersSchema = z.array(qsFilterSchema);

export const topLevelFiltersSchema = z.object({
  endpoints: filtersSchema.optional().catch([]),
  users: filtersSchema.optional().catch([]),
  inboxes: filtersSchema.optional().catch([]),
});

export type TopLevelFilters = z.infer<typeof topLevelFiltersSchema>;

export function getOdataId(definition: OdataDefinition, prefix?: string) {
  return [
    prefix,
    definition.scope,
    definition.id,
    ...(definition.with
      ? Object.entries(definition.with)
          .map(([key, value]) => `${key}=${value}`)
          .sort()
      : []),
  ]
    .filter(Boolean)
    .join(":");
}

export function parseOdataId(encodedOdataId: string) {
  const parts = encodedOdataId.split(":");
  const lastWith = parts.findLastIndex((str) => str.includes("="));
  const match = lastWith === -1 ? parts.at(-1) : parts[lastWith - 1];
  return match ?? encodedOdataId;
}
